import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, first, of, switchMap, tap } from 'rxjs';
import { CountrySearch } from '../countries/countries.model';
import { logRequest } from '../shared/http-utils';
import { ICountry } from '../shared/model/address.model';
import { CountryStore } from '../store/country.store';

@Injectable({
    providedIn: 'root',
})
export class CountryService {
    baseUrl = '/school-service/api/countries';
    http = inject(HttpClient);
    store = inject(CountryStore);

    getCountries(): Observable<ICountry[]> {
        if (!this.store.loaded()) {
            return this.http
                .get<ICountry[]>(this.baseUrl)
                .pipe(tap(result => this.store.set(result)))
                .pipe(first());
        } else {
            return of(this.store.findAll()).pipe(first());
        }
    }

    get(id: string): Observable<ICountry> {
        if (!this.store.loaded()) {
            return this.http.get<ICountry[]>(this.baseUrl).pipe(
                tap(result => this.store.set(result)),
                switchMap(() => of(this.store.get(id)).pipe(first()))
            );
        } else {
            return of(this.store.get(id)).pipe(first());
        }
    }

    findByCode(code: string): Observable<ICountry> {
        if (!this.store.loaded()) {
            return this.http.get<ICountry[]>(this.baseUrl).pipe(
                tap(result => this.store.set(result)),
                switchMap(() => of(this.store.findCode(code)).pipe(first()))
            );
        } else {
            return of(this.store.findCode(code)).pipe(first());
        }
    }

    deleteCountry(country: ICountry): Observable<HttpResponse<any>> {
        return this.http
            .delete(`${this.baseUrl}/${country.id}`, { observe: 'response', headers: logRequest() })
            .pipe(tap(() => this.store.remove(country.id)));
    }

    createCountry(country: ICountry): Observable<HttpResponse<ICountry>> {
        return this.http
            .post<ICountry>(this.baseUrl, country, { observe: 'response', headers: logRequest() })
            .pipe(tap(() => this.store.create(country)));
    }

    updateCountry(country: ICountry): Observable<HttpResponse<ICountry>> {
        return this.http
            .put<ICountry>(this.baseUrl, country, { observe: 'response', headers: logRequest() })
            .pipe(tap(() => this.store.update(country)));
    }

    searchCountries(search: CountrySearch): Observable<ICountry[]> {
        if (!this.store.loaded()) {
            return this.http.get<ICountry[]>(this.baseUrl).pipe(
                tap(result => this.store.set(result)),
                switchMap(() => of(this.store.filter(search)).pipe(first()))
            );
        } else {
            return of(this.store.filter(search)).pipe(first());
        }
    }
}
